import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import api from "@/lib/api";

/**
 * First-run checklist.
 *
 * Disappears on its own once the required steps are done, rather than waiting
 * to be dismissed -- a panel that has to be closed manually is still there on
 * the day the team stops needing it. It renders nothing at all while loading,
 * so an established workspace never flashes a setup card it has finished with.
 */
export default function GettingStarted() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/tenants/onboarding")
      .then(({ data }) => { if (!cancelled) setState(data); })
      .catch(() => { if (!cancelled) setState({ complete: true }); });
    return () => { cancelled = true; };
  }, []);

  if (!state || state.complete) return null;

  const { steps, completed_count: done, required_count: total } = state;
  const next = steps.find((s) => !s.done);

  return (
    <section
      data-testid="getting-started"
      className="mb-6 overflow-hidden rounded-2xl border border-[#E4E9EF] bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E4E9EF] bg-gradient-to-r from-[#E8FBF1] to-[#C9F5E1] px-6 py-4">
        <div>
          <h2 className="font-display text-lg font-bold text-[#0B2B33]">Get set up</h2>
          <p className="text-sm text-[#43535E]">
            Three steps to your first published post.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="h-2 w-32 overflow-hidden rounded-full bg-white/70">
            <div
              className="h-full rounded-full bg-[#16A46B] transition-all duration-500"
              style={{ width: `${(done / total) * 100}%` }}
            />
          </div>
          <span className="text-sm font-semibold tabular-nums text-[#0B2B33]">
            {done} of {total}
          </span>
        </div>
      </div>

      <ol className="divide-y divide-[#E4E9EF]">
        {steps.map((s) => {
          const isNext = next && s.key === next.key;
          return (
            <li
              key={s.key}
              data-testid={`onboarding-step-${s.key}`}
              className={`flex flex-wrap items-center gap-4 px-6 py-4 ${isNext ? "bg-[#F6F8FA]" : ""}`}
            >
              <span
                aria-hidden="true"
                className={`grid h-7 w-7 shrink-0 place-items-center rounded-full border ${
                  s.done
                    ? "border-[#16A46B] bg-[#16A46B] text-white"
                    : isNext
                      ? "border-[#1F5FD8] text-[#1F5FD8]"
                      : "border-[#E4E9EF] text-[#8A97A0]"
                }`}
              >
                {s.done ? <Check className="h-4 w-4" /> : null}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`font-semibold ${s.done ? "text-[#8A97A0] line-through" : "text-[#0B2B33]"}`}>
                    {s.title}
                  </span>
                  {s.optional ? (
                    <span className="hs-chip bg-[#F6F8FA] text-[#8A97A0]">Optional</span>
                  ) : null}
                </div>
                {!s.done ? <p className="mt-0.5 text-sm text-[#43535E]">{s.body}</p> : null}
              </div>

              {!s.done ? (
                <Link
                  to={s.href}
                  data-testid={`onboarding-cta-${s.key}`}
                  className={
                    isNext
                      ? "hs-btn-primary inline-flex items-center gap-1.5 whitespace-nowrap"
                      : "inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-[#1F5FD8] hover:underline"
                  }
                >
                  {s.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              ) : (
                <span className="text-sm font-semibold text-[#16A46B]">Done</span>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
